"use client";

import {
  FC,
  memo,
  ReactNode,
  useEffect,
  useState,
  Suspense,
} from "react";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useSignupFlowReset } from "apps/website/hooks/useSignupFlowReset";
import DefaultHead from "apps/website/components/page/DefaultHead/DefaultHead";
import { makePageTags } from "apps/website/utils/website/head";
import Header from "apps/website/components/page/Header/Header";
import {
  HEADER_LINKS_NONE,
} from "apps/website/components/page/Header/Header.map";
import type { TDiscount } from "@./suf-store";
import {
  useSignupFlowStore,
  useFreshSignupFlowNavigation,
  SIGNUP_FLOW_PAGES,
  type SignupFlowStore,
} from "@./state";
import {
  SignupFlowSlug,
} from "libs/state/src/lib/stores/flows/fresh/signupFlow/constants";
import { getInterpolationVariables } from "libs/utils/sign-up/fresh";
import type { Gender } from "@./cat-calculations";
import { DiscountCodeType } from "@./suf-store";
import type {
  IAnnouncement,
} from "../../components/page/AnnouncementBanner/AnnouncementBanner";

import {
  formatDiscount,
} from "../../components/pages/flows/fresh/sign-up/Pricing/utils";
import {
  META_30_TRIAL_DISCOUNT_CODE,
} from "libs/discount-calculation/src/constants/meta-offer-discounts";
import Main from "../../components/layout/Main/Main";
import Footer from "../../components/page/Footer/Footer";
import { themeRootClassMap } from "../../maps/Theme.map";
import cx from "../../utils/misc/cx";
import {
  QueryParamHandler,
} from "../../components/flows/fresh/sign-up/QueryParamHandler/QueryParamHandler";
import {
  SignupLoadingScreen,
} from "../../components/flows/fresh/common/SignupLoadingScreen/SignupLoadingScreen";
import {
  ProgressSteps,
} from "../../components/flows/fresh/common/ProgressSteps/ProgressSteps";
import {
  useSignupSteps,
} from "../../components/feature/Navigation/SimpleProgressSteps/hooks/useSignupSteps";
import {
  TitleSection,
} from "../../components/flows/fresh/sign-up/TitleSection/TitleSection";
import type {
  TextTitleVariantsProps,
} from "../../components/base/Text/TextTitle/styles";
import SignupPromotionalBanner
  from "../../components/flows/fresh/sign-up/SignupPromotionalBanner/SignupPromotionalBanner";
import { ILayout } from "../layouts.map";
import { TFunctionOf, TranslationKeyOf } from "apps/website/types/i18n";

export type SignupFlowLayoutProps = Partial<ILayout>;

const INITIAL_SLUG = SignupFlowSlug.CAT_COUNT;
const LOGIN_LINK = { id: "g-0", name: "Login", to: "/login" };
const CAROUSEL_ROTATE_INTERVAL_MS = 2500;

const checkShouldRedirect = (
  isStoreInitialised: boolean,
  pathname: string,
  versionMismatchDetected: boolean,
  activeCatIdsLength: number,
): boolean => isStoreInitialised &&
  pathname !== INITIAL_SLUG &&
  (versionMismatchDetected || activeCatIdsLength === 0);

const getAnnouncementText = (
  discount: TDiscount,
  bannerT: TFunctionOf<"Forms.Fresh.SignUp.PromotionalBanner">,
): string => {
  const formattedDiscount = formatDiscount(discount);
  if (!formattedDiscount) {
    return bannerT("announcementFallback");
  }
  return discount.type === DiscountCodeType.ABSOLUTE_TOTAL
    ? bannerT("announcementAbsolute", { discount: formattedDiscount })
    : bannerT("announcementOff", { discount: formattedDiscount });
};

// This is currently used for the META_30_TRIAL discount code only (test variant)
const getMetaAnnouncements = (
  discount: TDiscount,
  bannerT: TFunctionOf<"Forms.Fresh.SignUp.PromotionalBanner">,
): IAnnouncement[] => {
  const formattedDiscount = formatDiscount(discount);
  const discountText = formattedDiscount
    ? bannerT("metaAnnouncementDiscount", { discount: formattedDiscount })
    : bannerT("metaAnnouncementDiscountFallback");

  return [
    { text: discountText, icon: "filledTag" },
    { text: bannerT("metaAnnouncementTreats"), icon: "gift" },
    { text: bannerT("metaAnnouncementDelivery"), icon: "truck" },
  ];
};

const getAnnouncements = (
  discount: TDiscount,
  bannerT: TFunctionOf<"Forms.Fresh.SignUp.PromotionalBanner">,
): IAnnouncement[] => {
  if (discount.discountCode === META_30_TRIAL_DISCOUNT_CODE) {
    return getMetaAnnouncements(discount, bannerT);
  }
  return [ { text: getAnnouncementText(discount, bannerT), icon: "filledTag" } ];
};

// Add any pages here where a separate title is used for single cat
const SINGLE_CAT_TITLE_OVERRIDES: Record<string, string> = {
  catParentDetails: "catParentDetailsSingleCat",
};

const resolvePageTitle = (
  title: string | undefined,
  currentCat: { name?: string; gender?: Gender } | undefined,
  t: TFunctionOf<"Forms.Fresh.SignUp.Titles">,
  cats: SignupFlowStore["cats"],
  activeCatIds: string[],
): string | undefined => {
  if (!title) {
    return undefined;
  }

  if (!currentCat) {
    return t(title as TranslationKeyOf<"Forms.Fresh.SignUp.Titles">);
  }

  const names = activeCatIds
    .map((id) => cats[id]?.name)
    .filter((name): name is string => Boolean(name));

  // Use a single-cat title variant when available.
  const key = names.length === 1
    ? SINGLE_CAT_TITLE_OVERRIDES[title] ?? title
    : title;

  return t(key as TranslationKeyOf<"Forms.Fresh.SignUp.Titles">, getInterpolationVariables({
    name: currentCat.name, gender: currentCat.gender, names,
  }));
};

interface SignupFlowContentProps {
  showPromoBanner: boolean;
  announcements: IAnnouncement[];
  promoBannerAnimateOnEntry: boolean;
  isTransitionVisible: boolean;
  pathname: SignupFlowSlug;
  progressBarBgClass?: string;
  title?: string;
  titleSize?: NonNullable<TextTitleVariantsProps["size"]>;
  steps: { label: string; slugs: string[] }[];
  children: ReactNode;
}

const SignupFlowContent: FC<Readonly<SignupFlowContentProps>> = ({
  showPromoBanner,
  announcements,
  promoBannerAnimateOnEntry,
  isTransitionVisible,
  pathname,
  progressBarBgClass,
  title,
  titleSize,
  steps,
  children,
}) => (
  <>
    <div className="sticky top-[var(--header-height-mobile)] xl:top-[var(--header-height-desktop)] w-full z-20 bg-white">
      { showPromoBanner && (
        <SignupPromotionalBanner
          announcements={announcements}
          rotateIntervalMs={CAROUSEL_ROTATE_INTERVAL_MS}
          animateOnEntry={promoBannerAnimateOnEntry}
        />
      ) }
      <ProgressSteps
        hideProgressSteps={isTransitionVisible}
        pathname={pathname}
        steps={steps}
        bgColor={progressBarBgClass}
      />
    </div>
    { !isTransitionVisible && (
      <TitleSection
        title={title}
        titleSize={titleSize}
        bgClassName={progressBarBgClass || "bg-white"}
      />
    ) }
    { children }
  </>
);

const FreshSignupFlow: FC<Readonly<SignupFlowLayoutProps>> =
  ({ children, head, lastBuilt }) => {
    const t = useTranslations("Forms.Fresh.SignUp.Titles");
    const bannerT = useTranslations("Forms.Fresh.SignUp.PromotionalBanner");
    const signupSteps = useSignupSteps();
    const pathname = usePathname();
    const [ hideBackButton, setHideBackButton ] = useState(true);
    const [ mounted, setMounted ] = useState(false);

    const { canGoBack, goBack } = useFreshSignupFlowNavigation(pathname as SignupFlowSlug);

    const {
      cats,
      activeCatId,
      isStoreInitialised,
      versionMismatchDetected,
      getActiveCatIds,
      discount,
      isTransitionVisible,
    } = useSignupFlowStore((state) => ({
      activeCatId: state.activeCatId,
      cats: state.cats,
      isStoreInitialised: state.isStoreInitialised,
      versionMismatchDetected: state.versionMismatchDetected,
      getActiveCatIds: state.getActiveCatIds,
      discount: state.subscription.discount,
      isTransitionVisible: state.viewState.catParentDetails?.isTransitionVisible ?? false,
    }));

    // Synchronously check if we should redirect (prevents rendering with invalid state)
    const shouldRedirect = checkShouldRedirect(
      isStoreInitialised,
      pathname,
      versionMismatchDetected,
      getActiveCatIds().length,
    );

    const currentCat = cats[activeCatId];


    const {
      title,
      titleSize,
      hideFooter = true,
      bgColor = "light-grey",
      hidePromoBanner = false,
      promoBannerAnimateOnEntry = false,
      progressBarBgColor,
    } = SIGNUP_FLOW_PAGES[pathname as SignupFlowSlug];

    useEffect(() => {
      setMounted(true);
    }, []);

    useEffect(() => {
      setHideBackButton(pathname === INITIAL_SLUG);
    }, [ pathname ]);

    useSignupFlowReset();

    const showPromoBanner = !hidePromoBanner && !isTransitionVisible;
    const progressBarBgClass = progressBarBgColor ? themeRootClassMap[progressBarBgColor] : undefined;
    const announcements = getAnnouncements(discount, bannerT);

    const pageTitle = resolvePageTitle(title, currentCat, t, cats, getActiveCatIds());

    return (
      <div>
        <DefaultHead
          seo={makePageTags(head?.seo)}
          /* @ts-ignore */
          hideFromSearch={head?.hide_from_search_engines}
          lastBuilt={lastBuilt}
          fallbackTags={head?.fallbackTags && makePageTags(head.fallbackTags)}
        />
        <Suspense>
          <QueryParamHandler />
        </Suspense>

        <Header
          {...HEADER_LINKS_NONE}
          hasAnnouncementBanner={false}
          guestLinks={hideBackButton ? [ LOGIN_LINK ] : []}
          historyNavigation={{
            back: () => (canGoBack ? goBack() : undefined),
            hide: hideBackButton,
          }}
          backgroundColor="light"
        />

        <Main className={cx(themeRootClassMap[bgColor], "min-h-[calc(100dvh-var(--header-height-mobile))] xl:min-h-[calc(100dvh-var(--header-height-desktop))]")}>
          { !mounted || !isStoreInitialised || shouldRedirect ? (
            <SignupLoadingScreen />
          ) : (
            <SignupFlowContent
              showPromoBanner={showPromoBanner}
              announcements={announcements}
              promoBannerAnimateOnEntry={promoBannerAnimateOnEntry}
              isTransitionVisible={isTransitionVisible}
              pathname={pathname as SignupFlowSlug}
              progressBarBgClass={progressBarBgClass}
              title={pageTitle}
              titleSize={titleSize}
              steps={signupSteps}
            >
              { children }
            </SignupFlowContent>
          ) }
        </Main>

        { !hideFooter && (
          <Footer />
        ) }
      </div>
    );
  };

export default memo(FreshSignupFlow);
